import { useEffect, useState } from "react";
import { AlertTriangle, Loader2, CloudOff, Cloud } from "lucide-react";
import { useConnection, useSiteStore, useSnapshot } from "@/store/site";
import { useNow } from "@/hooks/useNow";
import { useT } from "@/lib/i18n";
import { formatHourMin, formatRelativeAge, formatTimeOfDay } from "@/lib/format";
import { cn } from "@/lib/cn";
import { freshnessBucket } from "@/lib/selectors/freshnessBucket";
import { SmartEMark } from "@/components/ui/SmartEMark";
import { reload, switchToMock, retryLive } from "@/lib/connectionRecovery";

/**
 * Full-content overlay shown while the SPA is waiting on TB to deliver
 * the first live snapshot (`mode === "live" && connection === "connecting"`).
 *
 * Why this exists: previously the SPA seeded the live spark charts with
 * mock data ("preserves history, avoids flash of empty"), but operators
 * found the mock-data flicker confusing — for a few seconds the page
 * looked like it was showing real values, then snapped to different
 * numbers when TB caught up. Hiding everything behind a deliberate
 * "Connecting…" splash is honest and avoids the misread.
 *
 * The splash also surfaces the error state when discovery fails (TB
 * unreachable, bad JWT, no accessible assets) — the operator can choose
 * to switch to mock to keep working, or reload to retry.
 *
 * When the operator DOES click "Use mock data" after a live failure
 * (`liveErrorAt` set, mode mock via ?mock=1), the splash gives way to a
 * persistent slim amber banner so the simulated values on every page
 * can never be misread as the real site.
 */
export function ConnectingSplash() {
  const { mode, connection, error } = useConnection();
  const t = useT();
  const liveErrorAt = useSiteStore((s) => s.liveErrorAt);
  // Mock/demo mode: no splash — but if mock was entered AFTER a live
  // failure, pin the SIMULATED-DATA banner instead. The snapshot-level
  // outage strip rides along too, stacking BELOW the banner when both
  // are visible.
  if (mode !== "live") {
    return (
      <>
        {liveErrorAt !== null && <SimulatedDataBanner failedAt={liveErrorAt} />}
        <TelemetryOutageStrip below={liveErrorAt !== null} />
      </>
    );
  }
  // Don't show when live data is flowing (the page renders normally).
  // "reconnecting" deliberately does NOT mount the splash: a socket blip
  // must not blank a readable (frozen-but-real) page — the amber topbar
  // pill + toasts carry that state (DATA-02). The outage strip still
  // rides along on these readable pages; the connecting/error splash
  // below covers the whole page, so those states skip the strip.
  if (connection === "live" || connection === "mock" || connection === "reconnecting") {
    return <TelemetryOutageStrip below={false} />;
  }

  const isError = connection === "error";
  const isEmbedded = typeof window !== "undefined" && window.parent !== window;

  return (
    <div className="absolute inset-0 z-30 grid place-items-center bg-bg/95 backdrop-blur-sm">
      <div className="m-6 max-w-md rounded-xl border border-edge bg-surface p-6 text-center shadow-xl">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center">
          {isError ? (
            <CloudOff size={48} className="text-critical" />
          ) : (
            <div className="relative">
              <SmartEMark size={48} />
              <Loader2 size={64} className="absolute -left-2 -top-2 animate-spin text-accent/40" />
            </div>
          )}
        </div>
        {isError ? (
          <>
            <h2 className="text-base font-semibold text-critical">{t("monitor.splash.liveUnavailable")}</h2>
            <p className="mt-2 text-xs text-text-muted">{error ?? t("monitor.splash.discoveryFailed")}</p>
            <ConnectingHints embedded={isEmbedded} />
            <RecoveryActions />
          </>
        ) : (
          <>
            <h2 className="text-base font-semibold">
              <Cloud size={14} className="mr-1.5 inline -translate-y-0.5 text-accent" />
              {t("monitor.splash.connecting")}
            </h2>
            <p className="mt-2 text-xs text-text-muted">
              {t("monitor.splash.waitingAuth")}
            </p>
            <ConnectingTimer />
            <ConnectingHints embedded={isEmbedded} />
          </>
        )}
      </div>
    </div>
  );
}

/** Small live counter so the operator can see the page isn't frozen. */
function ConnectingTimer() {
  const t = useT();
  const [s, setS] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => setS((x) => x + 1), 500);
    return () => window.clearInterval(timer);
  }, []);
  return (
    <p className="mt-3 font-mono text-[10px] text-text-faint">
      {t("monitor.splash.elapsed", { s: (s / 2).toFixed(1) })}
    </p>
  );
}

function ConnectingHints({ embedded }: { embedded: boolean }) {
  const t = useT();
  return (
    <div className="mt-4 rounded-md border border-edge bg-surface-2 p-3 text-left text-[10px] leading-relaxed text-text-muted">
      {embedded ? (
        <>{t("monitor.splash.embeddedHint")}</>
      ) : (
        <>
          {t("monitor.splash.devHint.pre")}
          {" "}<code className="rounded bg-surface px-1">?live=1&amp;tb=…&amp;token=…</code>
          {" "}{t("monitor.splash.devHint.mid")}{" "}<code className="rounded bg-surface px-1">?mock=1</code>
          {" "}{t("monitor.splash.devHint.post")}
        </>
      )}
    </div>
  );
}

function RecoveryActions() {
  const t = useT();
  // reload / switchToMock are the shared seam (G33) — see lib/connectionRecovery.
  return (
    <div className="mt-4 flex flex-wrap justify-center gap-2">
      <button
        onClick={reload}
        className="flex h-9 items-center gap-1.5 rounded-md border border-accent/40 bg-accent/15 px-3 text-xs font-semibold text-accent hover:bg-accent/25"
      >
        {t("common.retry")}
      </button>
      <button
        onClick={switchToMock}
        className="flex h-9 items-center gap-1.5 rounded-md border border-edge bg-surface px-3 text-xs hover:border-accent/40"
      >
        {t("monitor.splash.useMock")}
      </button>
    </div>
  );
}

/**
 * Slim full-width amber banner pinned below the topbar whenever the data
 * on screen is simulated BECAUSE a live connection failed (the operator
 * clicked "Use mock data" on the error splash). Fixed on every page so
 * the synthetic values are unmistakable even while scrolling; Retry
 * reverses the ?mock=1 navigation to re-attempt the live connection.
 *
 * `fixed` (not `sticky`): the splash mounts inside <main>, whose
 * overflow-x-hidden would scope a sticky banner to a scrollport that
 * never scrolls (the document does). The in-flow spacer keeps the fixed
 * strip from overlapping the top of the page content.
 */
function SimulatedDataBanner({ failedAt }: { failedAt: number }) {
  const t = useT();
  // retryLive is the shared seam (G33) — inverse of switchToMock; see
  // lib/connectionRecovery.
  return (
    <>
      <div aria-hidden className="h-8" />
      <div className="fixed inset-x-0 top-12 z-20 flex h-8 items-center justify-center gap-3 border-b border-warn/40 bg-warn/15 px-3 text-[11px] font-semibold text-warn backdrop-blur-sm">
        <AlertTriangle size={13} className="shrink-0" />
        <span>{t("monitor.splash.simulatedBanner", { time: formatHourMin(failedAt) })}</span>
        <button
          onClick={retryLive}
          className="rounded border border-warn/40 bg-warn/10 px-2 py-0.5 text-[11px] font-semibold text-warn hover:bg-warn/25"
        >
          {t("common.retry")}
        </button>
      </div>
    </>
  );
}

/**
 * Slim strip pinned below the topbar whenever the SNAPSHOT-level
 * connection signal degrades: the TB assembler stamps "stale" when the
 * newest telemetry source is older than 240 s (2× the ~2-min uplink
 * cadence) and "offline" beyond that; the mock adapter's setOutage
 * debug knob mirrors the same degradation. Renders in ANY mode so the
 * simulated outage is testable forever — the caller only skips it while
 * the connecting/error splash already covers the page.
 *
 * No retry button on purpose: the signal self-heals the moment fresh
 * telemetry lands (the assembler recomputes per flush).
 *
 * Same fixed/spacer pattern as {@link SimulatedDataBanner} (see its
 * comment for why `fixed`, not `sticky`); `below` stacks this strip
 * under that banner (top-12 → top-20), each contributing its own
 * in-flow h-8 spacer so page content clears both.
 */
function TelemetryOutageStrip({ below }: { below: boolean }) {
  const snap = useSnapshot();
  const { connection } = useConnection();
  const t = useT();
  // 1 s tick keeps the "(N min ago)" age honest while frozen data sits
  // on screen — exactly when nothing else would trigger a re-render.
  const now = useNow(1000);

  // G24: debounce the SPOKEN text so a flap at the 240s stale boundary
  // doesn't re-announce on every toggle. The VISIBLE icon+text still
  // appears immediately (driven by the early-return + offline check
  // below); only the assertive announcement waits out a ~1.5s window.
  const degraded = snap.connection === "stale" || snap.connection === "offline";
  const [announce, setAnnounce] = useState(false);
  useEffect(() => {
    if (!degraded) {
      // Returned to a non-degraded state — drop the announcement.
      setAnnounce(false);
      return;
    }
    // Only announce after the degraded state has held continuously ≥1.5s.
    const timer = window.setTimeout(() => setAnnounce(true), 1500);
    return () => window.clearTimeout(timer);
    // Keyed on snap.connection: a stale↔offline↔live flap restarts the
    // timer, so a boundary flutter never crosses the announce threshold.
  }, [snap.connection, degraded]);

  if (snap.connection !== "stale" && snap.connection !== "offline") return null;
  const offline = snap.connection === "offline";
  // G29: tone flows from the shared freshnessBucket() projection so the
  // strip can never drift from the pill's tone vocabulary. offline→down
  // (critical) and stale→aging (warn) reproduce today's colours exactly.
  const view = freshnessBucket({ connection, snapshot: snap.connection, hasError: false });
  const message = t(offline ? "monitor.outage.offline" : "monitor.outage.stalled", {
    time: formatTimeOfDay(snap.ts),
    age: formatRelativeAge(now - snap.ts),
  });
  return (
    <>
      <div aria-hidden className="h-8" />
      <div
        role="status"
        aria-live="assertive"
        aria-atomic="true"
        className={cn(
          "fixed inset-x-0 z-20 flex h-8 items-center justify-center gap-3 border-b px-3 text-[11px] font-semibold backdrop-blur-sm",
          below ? "top-20" : "top-12",
          view.tone,
        )}
      >
        {offline
          ? <CloudOff size={13} className="shrink-0" />
          : <AlertTriangle size={13} className="shrink-0" />}
        {/* Visible immediately; aria-hidden so the spoken announcement is
            driven solely by the debounced sr-only span below. */}
        <span aria-hidden>{message}</span>
        {/* Spoken only after the debounce window has elapsed. */}
        {announce && <span className="sr-only">{message}</span>}
      </div>
    </>
  );
}
